// src/hooks/use-clipping-settings.ts
import { useCallback } from 'react';
import type { ClipBox } from '@/helper/clipping-box-geometry';

export interface ClippingSettings {
	box: ClipBox | null;
}

// Version 1 stored bounds relative to a centre recomputed from the loaded layers,
// which moves whenever the layers change, so those boxes cannot be placed again.
// Version 2 was only written by development versions of the 2026-10 rework, whose boxes came
// from placements since replaced; they are dropped once.
const VERSION = 3;

const DEFAULT_SETTINGS: ClippingSettings = {
	box: null,
};

export const useClippingSettings = (assetId: string | undefined) => {
	const storageKey = `clipping_box_settings_${assetId || 'default'}`;

	const getSettings = useCallback((): ClippingSettings => {
		try {
			const saved = localStorage.getItem(storageKey);
			if (!saved) return DEFAULT_SETTINGS;
			const parsed = JSON.parse(saved);
			if (parsed?.version !== VERSION) return DEFAULT_SETTINGS;
			return { box: parsed.box ?? null };
		} catch (e) {
			console.warn('Failed to parse clipping settings', e);
			return DEFAULT_SETTINGS;
		}
	}, [storageKey]);

	const updateSettings = useCallback(
		(newSettings: Partial<ClippingSettings>) => {
			const updated = { ...getSettings(), ...newSettings };
			// Called at the end of a box drag, before the camera controls are switched back on:
			// a full or blocked storage must not throw there.
			try {
				localStorage.setItem(storageKey, JSON.stringify({ version: VERSION, ...updated }));
			} catch (e) {
				console.warn('Failed to save clipping settings', e);
			}
		},
		[getSettings, storageKey]
	);

	return {
		getSettings,
		updateSettings,
	};
};
